import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PloegInschrijvingFields } from "./PloegInschrijvingFields";
import { useInschrijvingActies, type Inschrijving } from "./useInschrijving";

/**
 * Carries the whole inschrijving rather than a bare ploeg: the API enters name and klassen in one
 * transaction, so a refused combination leaves nothing behind and is corrected in place.
 */
export function NieuwePloeg({
  inschrijving,
  korpsId,
  korpsName,
  gesloten,
}: {
  inschrijving: Inschrijving;
  korpsId: string;
  korpsName: string;
  gesloten: boolean;
}) {
  const { t } = useTranslation("ploegen");
  const { aanmelden } = useInschrijvingActies(
    inschrijving.inschrijfperiodeId,
    korpsId,
  );
  const [open, setOpen] = useState(false);

  function wissel(volgende: boolean) {
    setOpen(volgende);
    if (!volgende) aanmelden.reset();
  }

  return (
    <Dialog open={open} onOpenChange={wissel}>
      <DialogTrigger asChild>
        <Button disabled={gesloten}>
          <Plus />
          {t("nieuw.trigger")}
        </Button>
      </DialogTrigger>
      {/* Not focusing the ploegnaam is `DialogContent`'s own doing now - see lib/focus.ts. */}
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("nieuw.title")}</DialogTitle>
          <DialogDescription>
            {t("nieuw.description", {
              korps: korpsName,
              periode: inschrijving.inschrijfperiodeName,
            })}
          </DialogDescription>
        </DialogHeader>

        {/* Keyed on whether the modal is open, so every visit starts on an empty form rather
            than on what was abandoned last time. */}
        <PloegInschrijvingFields
          key={String(open)}
          id="nieuwe-ploeg"
          inschrijving={inschrijving}
          standaardNaam={inschrijving.nextPloegName}
          submitLabel={t("nieuw.submit")}
          pending={aanmelden.isPending}
          error={aanmelden.error}
          gesloten={gesloten}
          onSubmit={(velden) =>
            aanmelden.mutate(velden, { onSuccess: () => wissel(false) })
          }
        />
      </DialogContent>
    </Dialog>
  );
}
